import { endsWith } from 'lodash/fp';
import { Action } from 'redux';
import { ThunkMiddleware } from 'redux-thunk';
import { ApplicationState, ThunkContext } from '@mews-commander/types';
import { getOrFalse } from '@mews-commander/utils';
import {
    AbortError,
    AdditionalAuthorizationError,
    BadRequestError,
    BusinessError,
    lt,
    NetworkError,
    NotAuthorizedError,
    QuotaExceededError,
    RequestEntityTooLarge,
    ResponseError,
    showErrorAlert,
    TimeoutError,
} from '@mews/framework';
import { connectionEstablished, connectionLost } from '../core/connection/actions';
import { showAdditionalAuthorizationModal } from '../modules/auth/screens/AdditionalAuthorization/additionalAuthorizationStore';

const errorHandler: ThunkMiddleware<ApplicationState, Action, ThunkContext> = (store) => (next) => (action) => {
    if (action && action.error) {
        if (action.payload instanceof NetworkError || action.error.name === NetworkError.name) {
            store.dispatch(connectionLost());
        }
    }

    const isSuccessFetchAction = !!getOrFalse('meta.isFetchAction', action) && endsWith('_SUCCEEDED', action.type);
    if (isSuccessFetchAction) {
        store.dispatch(connectionEstablished());
    }

    const isBackgroundAction = !!getOrFalse('meta.isBackgroundAction', action);
    if (isBackgroundAction) {
        return next(action);
    }

    if (action && action.error) {
        handleError(action);
    }

    return next(action);
};

const handleError = ({ payload, error }: $TSFixMe) => {
    const action = { payload, error };

    if (hasError(action, ResponseError)) {
        showErrorAlert({ description: lt('CommunicationWithServerFailed') });
    } else if (hasError(action, TimeoutError)) {
        showErrorAlert({ description: lt('TimeoutErrorMessage') });
    } else if (hasError(action, RequestEntityTooLarge)) {
        showErrorAlert({ description: lt('FileTooLargeErrorMessage') });
    } else if (hasError(action, AdditionalAuthorizationError)) {
        showAdditionalAuthorizationModal();
    } else if (
        hasError(action, BusinessError) ||
        hasError(action, NotAuthorizedError) ||
        hasError(action, BadRequestError)
    ) {
        const message = getErrorMessage(action);
        showErrorAlert({ description: <>{message}</> });
    } else if (hasError(action, NetworkError)) {
        showErrorAlert({ description: lt('CommunicationWithServerFailed') });
    } else if (hasError(action, QuotaExceededError)) {
        showErrorAlert({ description: lt('TooManyRequestsErrorMessage') });
    } else if (action.payload && !hasError(action, AbortError)) {
        showErrorAlert({ description: lt('FatalErrorMessage') });
    }
};

export const handleErrorPure = (error: Error) => handleError({ payload: error, error });

const hasError = ({ payload, error }: $TSFixMe, ErrorType: $TSFixMe) =>
    payload instanceof ErrorType || error.name === ErrorType.name;

const getErrorMessage = ({ payload, error }: $TSFixMe) => {
    if (payload && payload.message) {
        return payload.message;
    }

    return error.message;
};

export default errorHandler;
